import React, { useCallback, useRef, useState, useEffect, useMemo } from "react";
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import ReactFlow, {
  ReactFlowInstance,
  useEdgesState,
  useNodesState,
  useReactFlow,
  Controls,
  MiniMap,
  Connection,
  Edge,
  Node,
  MarkerType,
  addEdge,
} from "reactflow";
import "reactflow/dist/style.css";
import "./ContextMenu.css";

import CustomNode from "./CustomNode";
import AnnotationNode from "./AnnotationNode";
import GroupNode from "./GroupNode";
import SettingsPanel from "./SettingsPanel";
import RightSettingsDrawer from "./RightSettingsDrawer";
import PatternsLibrary from "./PatternsLibrary";
import MultiDomainWizardModal from "./MultiDomainWizardModal";
import ValidationPanel from "./ValidationPanel";
import FinOpsPanel from "./FinOpsPanel";
import FabricLineagePanel from "./FabricLineagePanel";
import VariablesPanel from "./VariablesPanel";
import ConnectionSuggestionsDrawer from "./ConnectionSuggestionsDrawer";
import ConnectionSuggestionsButton from "./ConnectionSuggestionsButton";
import ConfirmDialog from "./ConfirmDialog";
import DependencyDetector from "./DependencyDetector";
import EdgeSettingsPanel from "./EdgeSettingsPanel";
import { useDependencyDetection } from "../../hooks/useDependencyDetection";

import { azureIconsByType } from "../../Config/azure-iconsByType";
import { azureIconsByTypeTop100 } from "../../Config/azure/azure-iconsByType.top100";
import { validateCanvas, ValidationIssue } from "../../utils/resourceValidator";
import { getConnectionSuggestions, ConnectionSuggestion } from "../../utils/connectionSuggestions";
import { awsIconsByTypeTop100 } from "../../Config/aws/aws-iconsByType.top100";
import { gcpIconsByTypeTop100 } from "../../Config/gcp/gcp-iconsByType.top100";
import { fabricIconsByTypeTop100 } from "../../Config/fabric/fabric-iconsByType.top100";

import { useDiagramSettings } from "./DiagramSettingsContext";
import defaultParamsByType from "../../Config/defaultParamsByType";
import { useHistory } from "../../hooks/useHistory";
import { useArchitectureTabs } from "../../context/ArchitectureTabsContext";

import { FiSettings, FiEdit2, FiCopy, FiTrash2, FiRotateCcw, FiRotateCw, FiRefreshCw, FiAlignLeft, FiAlignCenter, FiAlignRight, FiMessageSquare, FiPackage, FiCheckSquare, FiSliders, FiAlertTriangle, FiLayers, FiDollarSign, FiGitMerge, FiSave } from "react-icons/fi";
import azureDefaultIcon from "../Assets/azure.svg";

const nodeTypes = {
  custom: CustomNode,
  annotation: AnnotationNode,
  group: GroupNode
};
const getId = () => Math.random().toString(36).substr(2, 9);


/** Returns the optimal source/target handle IDs based on relative node positions. */
function getOptimalHandles(srcPos: {x: number; y: number}, tgtPos: {x: number; y: number}) {
  const dx = tgtPos.x - srcPos.x;
  const dy = tgtPos.y - srcPos.y;
  if (Math.abs(dx) > Math.abs(dy)) {
    return dx > 0
      ? { sourceHandle: 'right-source', targetHandle: 'left-target' }
      : { sourceHandle: 'left-source', targetHandle: 'right-target' };
  }
  return dy > 0
    ? { sourceHandle: 'bottom-source', targetHandle: 'top-target' }
    : { sourceHandle: 'top-source', targetHandle: 'bottom-target' };
}

/** Returns the absolute canvas position for a node — recursive for multi-level nesting. */
function getAbsolutePos(n: Node, allNodes: Node[]): {x: number; y: number} {
  if (!(n as any).parentNode) return n.position;
  const parent = allNodes.find(p => p.id === (n as any).parentNode);
  if (!parent) return n.position;
  const parentAbs = getAbsolutePos(parent, allNodes);
  return { x: n.position.x + parentAbs.x, y: n.position.y + parentAbs.y };
}


function resolveIconFromType(type: string): string {
  if (type.startsWith('aws_')) {
    return (awsIconsByTypeTop100 as any)?.[type] || '/icons/aws/Arch_General_48.svg';
  }
  if (type.startsWith('google_')) {
    return (gcpIconsByTypeTop100 as any)?.[type] || '/icons/gcp/google-cloud.svg';
  }
  if (type.startsWith('fabric_')) {
    return (fabricIconsByTypeTop100 as any)?.[type] || '/icons/fabric/fabric_48_color.svg';
  }
  return (
    (azureIconsByTypeTop100 as any)?.[type] ||
    (azureIconsByType as any)?.[type] ||
    azureDefaultIcon
  );
}

export default function FlowEditor() {
  const { t } = useTranslation('canvas');
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const rf = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState<Node[]>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge[]>([]);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);
  const [selectedNodes, setSelectedNodes] = useState<string[]>([]);

  // Connection preview overlay state
  const [connPreview, setConnPreview] = useState<{
    startX: number; startY: number; curX: number; curY: number;
  } | null>(null);
  const connPreviewRef = useRef<{ startX: number; startY: number } | null>(null);

  const onConnectStart = useCallback((event: any) => {
    const rect = reactFlowWrapper.current?.getBoundingClientRect();
    if (!rect) return;
    const clientX = event?.clientX ?? event?.touches?.[0]?.clientX ?? 0;
    const clientY = event?.clientY ?? event?.touches?.[0]?.clientY ?? 0;
    connPreviewRef.current = { startX: clientX - rect.left, startY: clientY - rect.top };
    setConnPreview({ startX: clientX - rect.left, startY: clientY - rect.top, curX: clientX - rect.left, curY: clientY - rect.top });
  }, []);

  const onConnectEnd = useCallback(() => {
    connPreviewRef.current = null;
    setConnPreview(null);
  }, []);

  // Track mouse during connection drag — updates preview line endpoint
  const onCanvasMouseMove = useCallback((e: React.MouseEvent) => {
    if (!connPreviewRef.current) return;
    const rect = reactFlowWrapper.current?.getBoundingClientRect();
    if (!rect) return;
    setConnPreview({
      startX: connPreviewRef.current.startX,
      startY: connPreviewRef.current.startY,
      curX: e.clientX - rect.left,
      curY: e.clientY - rect.top,
    });
  }, []);

  // Provider color derived from node types — used for both manual and AI edges
  const canvasEdgeColor = useMemo(() => {
    const types = nodes.map(n => (n.data as any)?.type || (n.data as any)?.resourceType || '');
    if (types.some((t: string) => t.startsWith('fabric_'))) return '#038387';
    if (types.some((t: string) => t.startsWith('aws_')))    return '#F97316';
    if (types.some((t: string) => t.startsWith('google_'))) return '#22C55E';
    return '#3B82F6';
  }, [nodes]);

  // Panneau flottant (petit)
  const [settingsNode, setSettingsNode] = useState<Node | null>(null);
  const [panelPosition, setPanelPosition] = useState<{ x: number; y: number } | null>(null);

  // Panneau docké (grand)
  const [dockNode, setDockNode] = useState<Node | null>(null);
  const dockOpen = !!dockNode;

  // Validation
  const [validationIssues, setValidationIssues] = useState<ValidationIssue[]>([]);
  const [showValidation, setShowValidation] = useState(true);

  // Connection suggestions
  const [connectionSuggestions, setConnectionSuggestions] = useState<{
    sourceNode: Node;
    targetNode: Node;
    suggestions: ConnectionSuggestion[];
  } | null>(null);
  const [showSuggestionsDrawer, setShowSuggestionsDrawer] = useState(false);

  // Variables panel
  const [showVariables, setShowVariables] = useState(false);

  // FinOps panel
  const [showFinOps, setShowFinOps] = useState(false);

  // Lineage panel
  const [showLineage, setShowLineage] = useState(false);

  // Confirmation dialogs
  const [showConfirmReset, setShowConfirmReset] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [nodeToDelete, setNodeToDelete] = useState<Node | null>(null);

  // Toolbar dragging
  const [toolbarPortalEl, setToolbarPortalEl] = useState<Element | null>(null);

  const { gridVisible, gridSize, backgroundColorEnabled, backgroundColor } = useDiagramSettings();

  // Tabs context for synchronization
  const { activeTabId, updateTabData, getActiveTab, markTabDirty } = useArchitectureTabs();
  const lastSyncedTabId = useRef<string | null>(null);
  const isLoadingFromTab = useRef(false);

  // History management
  const { takeSnapshot, undo, redo, canUndo, canRedo } = useHistory(nodes, edges);

  // Dependency detection
  const activeTab = getActiveTab();
  const {
    missingResources,
    suggestions,
    suggestedEdges,
    isAnalyzing: isAnalyzingDeps,
    hasIssues: hasDependencyIssues,
    analyzeCurrentDiagram,
    createMissingResources,
    initializeHierarchy,
    clearDetection,
    resetHierarchy
  } = useDependencyDetection({
    architectureId: activeTabId,
    provider: activeTab?.provider || 'azure',
    enabled: true,
    autoInitializeHierarchy: true
  });

  const [showDependencyPanel, setShowDependencyPanel] = useState(false);

  // Edge settings
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);

  // Templates panel
  const [showTemplatesPanel, setShowTemplatesPanel] = useState(false);
  const [showMultiDomainWizard, setShowMultiDomainWizard] = useState(false);

  // Sync nodes/edges FROM tab context when active tab changes
  useEffect(() => {
    if (!activeTabId || activeTabId === lastSyncedTabId.current) return;

    const activeTab = getActiveTab();
    if (activeTab && !activeTab.isLoading) {
      console.log('🔄 Syncing from tab context:', activeTabId, 'nodes:', activeTab.nodes.length);
      isLoadingFromTab.current = true;
      setNodes(activeTab.nodes || []);
      setEdges(activeTab.edges || []);
      lastSyncedTabId.current = activeTabId;

      // Reset the flag after a short delay
      setTimeout(() => {
        isLoadingFromTab.current = false;
      }, 100);
    }
  }, [activeTabId, getActiveTab, setNodes, setEdges]);

  // Sync nodes/edges TO tab context when they change (debounced)
  useEffect(() => {
    // Don't sync if we're currently loading from tab (avoid infinite loop)
    if (isLoadingFromTab.current || !activeTabId) return;

    const timeout = setTimeout(() => {
      if (nodes.length > 0 || edges.length > 0) {
        console.log('💾 Syncing to tab context:', activeTabId, 'nodes:', nodes.length);
        updateTabData(activeTabId, nodes, edges);
      }
    }, 300); // Debounce to avoid too many updates

    return () => clearTimeout(timeout);
  }, [nodes, edges, activeTabId, updateTabData]);

  // Track node changes for history
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (nodes.length > 0 || edges.length > 0) {
        takeSnapshot(nodes, edges);
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [nodes, edges]);

  // Run validation when nodes or edges change
  useEffect(() => {
    const timeout = setTimeout(() => {
      const issues = validateCanvas(nodes, edges);
      setValidationIssues(issues);
    }, 300);
    return () => clearTimeout(timeout);
  }, [nodes, edges]);

  // Analyze dependencies when nodes change
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (nodes.length > 0) {
        console.log('🔍 Triggering dependency analysis for', nodes.length, 'nodes');
        analyzeCurrentDiagram({ nodes, edges });
      }
    }, 300); // Debounce to avoid too many API calls
    return () => clearTimeout(timeout);
  }, [nodes, edges, analyzeCurrentDiagram]);

  // Auto-show dependency panel when issues are detected
  useEffect(() => {
    if (hasDependencyIssues && (missingResources.length > 0 || suggestions.length > 0)) {
      console.log('🚨 Showing dependency panel:', {
        missingResources: missingResources.length,
        suggestions: suggestions.length
      });
      setShowDependencyPanel(true);
    }
  }, [hasDependencyIssues, missingResources.length, suggestions.length]);

  // Helper to delete a group and unparent its children
  const deleteGroupWithChildren = useCallback((groupId: string) => {
    setNodes((nds) => {
      // Find the group being deleted
      const groupNode = nds.find((n) => n.id === groupId);
      if (!groupNode) return nds.filter((n) => n.id !== groupId);

      // Update children: remove parentNode and convert to absolute position
      return nds
        .filter((n) => n.id !== groupId) // Remove the group
        .map((n) => {
          if (n.parentNode === groupId) {
            // Calculate absolute position
            const absolutePosition = {
              x: n.position.x + groupNode.position.x,
              y: n.position.y + groupNode.position.y,
            };
            // Remove parent relationship
            const { parentNode, extent, expandParent, ...rest } = n as any;
            return {
              ...rest,
              position: absolutePosition,
            };
          }
          return n;
        });
    });
  }, [setNodes]);

  // Supprimer tous les nœuds et edges sélectionnés
  const deleteSelectedNodes = useCallback(() => {
    const selectedNodesList = nodes.filter(n => n.selected);
    const selectedEdgesList = edges.filter(e => e.selected);
    if (selectedNodesList.length === 0 && selectedEdgesList.length === 0) return;

    const groupsToDelete = selectedNodesList.filter(n => n.type === 'group');
    const regularNodesToDelete = selectedNodesList.filter(n => n.type !== 'group');

    groupsToDelete.forEach(group => deleteGroupWithChildren(group.id));

    if (regularNodesToDelete.length > 0) {
      const regularNodeIds = regularNodesToDelete.map(n => n.id);
      setNodes((nds) => nds.filter((n) => !regularNodeIds.includes(n.id)));
    }

    const allDeletedIds = selectedNodesList.map(n => n.id);
    setEdges((eds) => eds.filter((e) =>
      !selectedEdgesList.map(edge => edge.id).includes(e.id) &&
      !allDeletedIds.includes(e.source) &&
      !allDeletedIds.includes(e.target)
    ));
  }, [nodes, edges, setNodes, setEdges, deleteGroupWithChildren]);

  // Helper to enrich group nodes with proper callbacks
  const enrichGroupNodes = useCallback((nodes: Node[]) => {
    return nodes.map((node) => {
      if (node.type === 'group' && (!node.data?.onDelete || !node.data?.onUpdate)) {
        return {
          ...node,
          data: {
            ...node.data,
            onDelete: () => deleteGroupWithChildren(node.id),
            onUpdate: (label: string, color: string) => {
              setNodes((nds) =>
                nds.map((n) =>
                  n.id === node.id ? { ...n, data: { ...n.data, label, color } } : n
                )
              );
            },
          },
        };
      }
      return node;
    });
  }, [deleteGroupWithChildren, setNodes]);

  // Initialize cloud hierarchy when first node is added
  const hasInitializedHierarchy = useRef(false);
  useEffect(() => {
    // Only initialize when there's exactly 1 non-group node and no hierarchy yet
    const nonGroupNodes = nodes.filter(n => n.type !== 'group');

    if (nonGroupNodes.length === 1 && !hasInitializedHierarchy.current && activeTabId) {
      console.log('🏗️ Initializing cloud hierarchy...');
      hasInitializedHierarchy.current = true;

      initializeHierarchy({ nodes, edges }).then((updated) => {
        if (updated) {
          console.log('✅ Cloud hierarchy initialized:', {
            totalNodes: updated.nodes.length,
            groupNodes: updated.nodes.filter(n => n.type === 'group').length,
            regularNodes: updated.nodes.filter(n => n.type !== 'group').length
          });

          // Enrich group nodes with proper callbacks before setting them
          const enrichedNodes = enrichGroupNodes(updated.nodes);

          // Use functional update to avoid race conditions
          setNodes(() => enrichedNodes);
          setEdges(updated.edges);
        }
      });
    }

    // Reset when switching tabs or when all nodes are deleted
    if (nodes.length === 0) {
      hasInitializedHierarchy.current = false;
      resetHierarchy();
    }
  }, [nodes.length, activeTabId, resetHierarchy]);

  // Apply suggested edges from dependency analysis
  useEffect(() => {
    if (suggestedEdges && suggestedEdges.length > 0) {
      setEdges((currentEdges) => {
        // Filter out suggested edges that already exist (by source-target pair)
        const existingPairs = new Set(
          currentEdges.map(e => `${e.source}-${e.target}`)
        );

        const newEdges = suggestedEdges.filter(
          edge => !existingPairs.has(`${edge.source}-${edge.target}`)
        );

        if (newEdges.length > 0) {
          console.log(`🔗 Adding ${newEdges.length} suggested dependency edges`);
          return [...currentEdges, ...newEdges];
        }

        return currentEdges;
      });
    }
  }, [suggestedEdges, setEdges]);

  // Auto-open suggestions drawer when new suggestions appear
  useEffect(() => {
    if (connectionSuggestions && connectionSuggestions.suggestions.length > 0) {
      setShowSuggestionsDrawer(true);
    }
  }, [connectionSuggestions]);

  // Initialiser le portal target dans le slot du CanvasHeader
  useEffect(() => {
    const el = document.getElementById('canvas-toolbar-slot');
    if (el) setToolbarPortalEl(el);
  }, []);

  const onConnect = useCallback(
    (params: Edge | Connection) => {
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            type: 'smoothstep',
            style: { stroke: canvasEdgeColor, strokeWidth: 2 },
            markerEnd: { type: MarkerType.ArrowClosed, color: canvasEdgeColor },
          },
          eds
        )
      );

      // Check for connection suggestions
      const sourceNode = nodes.find(n => n.id === params.source);
      const targetNode = nodes.find(n => n.id === params.target);

      if (sourceNode && targetNode) {
        console.log('🔗 Connexion détectée:', {
          source: (sourceNode.data as any)?.type || (sourceNode.data as any)?.tfKey,
          target: (targetNode.data as any)?.type || (targetNode.data as any)?.tfKey
        });

        const suggestions = getConnectionSuggestions(sourceNode, targetNode);
        console.log(`📋 ${suggestions.length} suggestion(s) trouvée(s)`);

        if (suggestions.length > 0) {
          console.log('✅ Affichage du panneau de suggestions');
          setConnectionSuggestions({ sourceNode, targetNode, suggestions });
        } else {
          console.log('ℹ️ Aucune suggestion pour cette connexion');
        }
      }
    },
    [setEdges, nodes, canvasEdgeColor]
  );

  // Helper to find the innermost group at an absolute canvas position
  const findGroupAtPosition = useCallback((position: { x: number; y: number }, excludeId?: string): Node | undefined => {
    const matching = nodes.filter((node) => {
      if (node.type !== 'group' || node.id === excludeId) return false;
      const absPos = getAbsolutePos(node, nodes);
      const nodeWidth  = node.width  || 300;
      const nodeHeight = node.height || 200;
      return (
        position.x >= absPos.x &&
        position.x <= absPos.x + nodeWidth &&
        position.y >= absPos.y &&
        position.y <= absPos.y + nodeHeight
      );
    });
    if (matching.length === 0) return undefined;
    // Return the innermost (smallest area) group
    return matching.reduce((smallest, current) => {
      const sArea = (smallest.width || 300) * (smallest.height || 200);
      const cArea = (current.width  || 300) * (current.height  || 200);
      return cArea < sArea ? current : smallest;
    });
  }, [nodes]);

  // Resources that should be created as container/group nodes
  // GCP hierarchy: Organization → Folder → Project → Resources
  const containerResourceTypes = [
    // Azure containers
    'azurerm_resource_group',
    'azurerm_virtual_network',
    'azurerm_subnet',
    // AWS containers
    'aws_vpc',
    'aws_subnet',
    // GCP containers (hierarchy: Folder > Project > Network > Subnetwork)
    'google_folder',           // Can contain other folders and projects
    'google_project',          // Can contain resources
    'google_compute_network',
    'google_compute_subnetwork',
    // Fabric containers
    'fabric_capacity',
    'fabric_workspace',
    'fabric_folder',
  ];

  // Get container color based on resource type (with visual hierarchy)
  const getContainerColor = (resourceType: string): string => {
    // Azure - blue shades
    if (resourceType === 'azurerm_resource_group') return '#DBEAFE';
    if (resourceType === 'azurerm_virtual_network') return '#BFDBFE';
    if (resourceType === 'azurerm_subnet') return '#93C5FD';
    if (resourceType.startsWith('azurerm_')) return '#DBEAFE';

    // AWS - orange shades
    if (resourceType === 'aws_vpc') return '#FED7AA';
    if (resourceType === 'aws_subnet') return '#FDBA74';
    if (resourceType.startsWith('aws_')) return '#FED7AA';

    // GCP - green shades (darker for higher hierarchy)
    if (resourceType === 'google_folder') return '#A7F3D0';
    if (resourceType === 'google_project') return '#D1FAE5';
    if (resourceType === 'google_compute_network') return '#BBF7D0';
    if (resourceType === 'google_compute_subnetwork') return '#86EFAC';
    if (resourceType.startsWith('google_')) return '#D1FAE5';

    // Fabric - teal/green shades
    if (resourceType === 'fabric_capacity') return '#E0F7FA';
    if (resourceType === 'fabric_workspace') return '#E8F5E9';
    if (resourceType === 'fabric_folder') return '#F3E5F5';
    if (resourceType.startsWith('fabric_')) return '#FFF8E1';

    return '#F3F4F6'; // Gray default
  };

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      if (!reactFlowInstance || !reactFlowWrapper.current) return;

      const type =
        event.dataTransfer.getData("application/reactflow") ||
        event.dataTransfer.getData("text/plain");
      if (!type) return;

      const labelFromDrag = event.dataTransfer.getData("app/label");
      const iconFromDrag = event.dataTransfer.getData("app/icon");

      const label =
        labelFromDrag ||
        (type.startsWith("azurerm_") ? type.replace(/^azurerm_/, "") : type);

      const bounds = reactFlowWrapper.current.getBoundingClientRect();
      const absolutePosition = reactFlowInstance.project({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      });

      const icon = iconFromDrag || resolveIconFromType(type) || azureDefaultIcon;

      const id = getId();
      const defaultProps = defaultParamsByType[type] || {};

      // Check if this resource type should be a container
      const isContainerResource = containerResourceTypes.includes(type);

      // Check if dropped on a group (only for non-container resources)
      const parentGroup = !isContainerResource ? findGroupAtPosition(absolutePosition) : null;

      // Calculate position relative to parent using absolute canvas coords (handles nested groups)
      const parentAbsPos = parentGroup ? getAbsolutePos(parentGroup, nodes) : null;
      const position = parentAbsPos
        ? { x: absolutePosition.x - parentAbsPos.x, y: absolutePosition.y - parentAbsPos.y }
        : absolutePosition;

      let newNode: Node;

      if (isContainerResource) {
        // Create as a group/container node with Terraform resource data
        const containerColor = getContainerColor(type);
        newNode = {
          id,
          type: 'group',
          position,
          width: 400,
          height: 250,
          style: {
            backgroundColor: containerColor,
          },
          data: {
            label: label || type.replace(/^(azurerm_|aws_|google_)/, ''),
            color: containerColor,
            type, // Terraform resource type
            resourceType: type, // Pour la détection de dépendances
            icon,
            name: label,
            params: defaultProps,
            properties: defaultProps,
            config: defaultProps,
            onDelete: () => deleteGroupWithChildren(id),
            onUpdate: (newLabel: string, newColor: string) => {
              setNodes((nds) =>
                nds.map((n) =>
                  n.id === id
                    ? {
                        ...n,
                        style: { ...n.style, backgroundColor: newColor },
                        data: { ...n.data, label: newLabel, color: newColor, name: newLabel }
                      }
                    : n
                )
              );
            },
          },
        };
      } else {
        // Create as a regular custom node
        newNode = {
          id,
          type: "custom",
          position,
          ...(parentGroup && {
            parentNode: parentGroup.id,
            extent: 'parent' as const,
            expandParent: true
          }),
          data: {
            type,
            resourceType: type, // Pour la détection de dépendances
            icon,
            label,
            name: label, // Pour la détection de dépendances
            editing: false,
            params: defaultProps,
            properties: defaultProps, // Pour la détection de dépendances
            config: defaultProps, // Pour le générateur Terraform
            onUpdateLabel: (newLabel: string) => {
              setNodes((nds) =>
                nds.map((n) =>
                  n.id === id ? { ...n, data: { ...n.data, label: newLabel, editing: false } } : n
                )
              );
            },
          },
        };
      }

      setNodes((nds) => [...nds, newNode]);

      // Trigger immediate dependency analysis after adding new node
      console.log('🔍 New node added:', type, '- Triggering immediate dependency analysis');
      setTimeout(() => {
        const updatedNodes = [...nodes, newNode];
        analyzeCurrentDiagram({ nodes: updatedNodes, edges });
      }, 100);
    },
    [reactFlowInstance, setNodes, findGroupAtPosition, nodes, edges, analyzeCurrentDiagram]
  );

  // Define which container types can be nested inside others
  // GCP: Folder > Project > Network > Subnetwork
  // Azure: ResourceGroup > VNet > Subnet
  // AWS: VPC > Subnet
  const canBeNestedIn = (childType: string, parentType: string): boolean => {
    const nestingRules: Record<string, string[]> = {
      // GCP hierarchy
      'google_folder': ['google_folder'],
      'google_project': ['google_folder'],
      'google_compute_network': ['google_project', 'google_folder'],
      'google_compute_subnetwork': ['google_compute_network', 'google_project'],
      // Azure hierarchy
      'azurerm_virtual_network': ['azurerm_resource_group'],
      'azurerm_subnet': ['azurerm_virtual_network', 'azurerm_resource_group'],
      // AWS hierarchy
      'aws_subnet': ['aws_vpc'],
      // Fabric hierarchy: Capacity > Workspace > Folder
      'fabric_workspace': ['fabric_capacity', 'fabric_domain'],
      'fabric_domain':   ['fabric_capacity'],
      'fabric_folder':   ['fabric_workspace'],
    };
    return nestingRules[childType]?.includes(parentType) || false;
  };

  // Re-routes all edges connected to a moved node to optimal source/target handles
  const rerouteConnectedEdges = useCallback((nodeId: string) => {
    setEdges(eds => {
      const currentNodes = rf.getNodes();
      return eds.map(edge => {
        if (edge.source !== nodeId && edge.target !== nodeId) return edge;
        const srcNode = currentNodes.find(n => n.id === edge.source);
        const tgtNode = currentNodes.find(n => n.id === edge.target);
        if (!srcNode || !tgtNode) return edge;
        const srcPos = getAbsolutePos(srcNode, currentNodes);
        const tgtPos = getAbsolutePos(tgtNode, currentNodes);
        return { ...edge, ...getOptimalHandles(srcPos, tgtPos) };
      });
    });
  }, [rf, setEdges]);

  // Handle node drag stop to update parent relationships
  const onNodeDragStop = useCallback(
    (event: React.MouseEvent, node: Node) => {
      const nodeType = node.data?.type || node.data?.resourceType || '';

      // For group nodes, only allow nesting if it's a valid hierarchy
      if (node.type === 'group') {
        // Get absolute position using recursive helper (handles multi-level nesting)
        const absolutePosition = getAbsolutePos(node, nodes);

        // Find potential parent group
        const potentialParent = findGroupAtPosition(absolutePosition, node.id);
        const potentialParentType = potentialParent?.data?.type || potentialParent?.data?.resourceType || '';

        // Check if nesting is allowed
        if (potentialParent && canBeNestedIn(nodeType, potentialParentType)) {
          const currentParent = node.parentNode;
          if (potentialParent.id !== currentParent) {
            const potParentAbs = getAbsolutePos(potentialParent, nodes);
            setNodes((nds) =>
              nds.map((n) => {
                if (n.id === node.id) {
                  return {
                    ...n,
                    position: {
                      x: absolutePosition.x - potParentAbs.x,
                      y: absolutePosition.y - potParentAbs.y,
                    },
                    parentNode: potentialParent.id,
                    extent: 'parent' as const,
                    expandParent: true,
                  };
                }
                return n;
              })
            );
          }
        } else if (!potentialParent && node.parentNode) {
          // Moving out of a group - only if dropped outside all groups
          const { parentNode, extent, expandParent, ...rest } = node as any;
          setNodes((nds) =>
            nds.map((n) => {
              if (n.id === node.id) {
                return {
                  ...rest,
                  position: absolutePosition,
                };
              }
              return n;
            })
          );
        }
        rerouteConnectedEdges(node.id);
        return;
      }

      // Regular node handling (non-group)
      // Get absolute position using recursive helper
      const absolutePosition = getAbsolutePos(node, nodes);

      // Find if node is now inside a group
      const newParent = findGroupAtPosition(absolutePosition, node.id);

      // Check if parent has changed
      const currentParent = node.parentNode;

      if (newParent?.id !== currentParent) {
        setNodes((nds) =>
          nds.map((n) => {
            if (n.id === node.id) {
              if (newParent) {
                const newParentAbs = getAbsolutePos(newParent, nodes);
                // Moving into a group
                return {
                  ...n,
                  position: {
                    x: absolutePosition.x - newParentAbs.x,
                    y: absolutePosition.y - newParentAbs.y,
                  },
                  parentNode: newParent.id,
                  extent: 'parent' as const,
                  expandParent: true,
                };
              } else {
                // Moving out of a group
                const { parentNode, extent, expandParent, ...rest } = n as any;
                return {
                  ...rest,
                  position: absolutePosition,
                };
              }
            }
            return n;
          })
        );
      }

      // Re-route edges connected to the moved node
      rerouteConnectedEdges(node.id);
    },
    [nodes, findGroupAtPosition, setNodes, rerouteConnectedEdges]
  );

  const [contextMenu, setContextMenu] = useState<{
    node: Node | null;
    position: { x: number; y: number };
  }>({
    node: null,
    position: { x: 0, y: 0 },
  });

  const closeContextMenu = () =>
    setContextMenu({ node: null, position: { x: 0, y: 0 } });

  const handleNodeContextMenu = useCallback((event: React.MouseEvent, node: Node) => {
    event.preventDefault();
    if (!reactFlowWrapper.current) return;

    // Récupérer l'élément DOM du node cliqué
    const nodeElement = (event.target as HTMLElement).closest('.react-flow__node');
    if (!nodeElement) return;

    const wrapperRect = reactFlowWrapper.current.getBoundingClientRect();
    const nodeRect = nodeElement.getBoundingClientRect();

    // Positionner le menu EN BAS à DROITE du composant
    const position = {
      x: nodeRect.right - wrapperRect.left + 15, // 15px à droite du bord droit
      y: nodeRect.bottom - wrapperRect.top + 5, // 5px en dessous du bas
    };

    setContextMenu({ node, position });
  }, []);

  // 🔑 DOUBLE-CLIC pour ouvrir directement les propriétés
  const handleNodeDoubleClick = useCallback((event: React.MouseEvent, node: Node) => {
    event.preventDefault();

    // Fermer le menu contextuel
    closeContextMenu();

    // Fermer le panneau flottant s'il est ouvert
    setSettingsNode(null);
    setPanelPosition(null);

    // Ouvrir directement le panneau de propriétés docké
    setDockNode(node);
  }, []);

  const handleRename = () => {
    if (!contextMenu.node) return;
    const updated = nodes.map((n) =>
      n.id === contextMenu.node!.id
        ? {
            ...n,
            data: {
              ...n.data,
              editing: true,
              onUpdateLabel: (newLabel: string) => {
                setNodes((nds) =>
                  nds.map((node) =>
                    node.id === n.id ? { ...node, data: { ...node.data, label: newLabel, editing: false } } : node
                  )
                );
              },
            },
          }
        : n
    );
    setNodes(updated);
    closeContextMenu();
  };

  const handleDuplicate = () => {
    if (!contextMenu.node) return;
    const node = contextMenu.node;
    const newNode: Node = {
      ...node,
      id: getId(),
      position: { x: node.position.x + 40, y: node.position.y + 40 },
    };
    setNodes((nds) => [...nds, newNode]);
    closeContextMenu();
  };

  const handleDelete = () => {
    if (!contextMenu.node) return;
    setNodeToDelete(contextMenu.node);
    setShowConfirmDelete(true);
    closeContextMenu();
  };

  const confirmDelete = () => {
    if (!nodeToDelete) return;

    // If deleting a group, use special handler to unparent children
    if (nodeToDelete.type === 'group') {
      deleteGroupWithChildren(nodeToDelete.id);
      setEdges((eds) => eds.filter((e) => e.source !== nodeToDelete.id && e.target !== nodeToDelete.id));
    } else {
      // Normal node deletion
      setNodes((nds) => nds.filter((n) => n.id !== nodeToDelete.id));
      setEdges((eds) => eds.filter((e) => e.source !== nodeToDelete.id && e.target !== nodeToDelete.id));
    }

    // Trigger dependency analysis after deletion to detect missing resources
    setTimeout(() => {
      const updatedNodes = nodes.filter((n) => n.id !== nodeToDelete.id);
      const updatedEdges = edges.filter((e) => e.source !== nodeToDelete.id && e.target !== nodeToDelete.id);
      if (updatedNodes.length > 0) {
        analyzeCurrentDiagram({ nodes: updatedNodes, edges: updatedEdges });
      }
    }, 100);

    setShowConfirmDelete(false);
    setNodeToDelete(null);
  };

  const handleSettingsClick = () => {
    if (!contextMenu.node) return;

    // Si le dock est déjà ouvert pour ce node, ne rien faire
    if (dockNode && dockNode.id === contextMenu.node.id) {
      closeContextMenu();
      return;
    }

    // Fermer le panneau docké s'il est ouvert (pour éviter d'avoir 2 panneaux ouverts)
    setDockNode(null);

    // Ouvrir le panneau flottant
    setSettingsNode(contextMenu.node);
    setPanelPosition(contextMenu.position);
    closeContextMenu();
  };

  // Undo/Redo handlers
  const handleUndo = useCallback(() => {
    const previousState = undo();
    if (previousState) {
      setNodes(previousState.nodes);
      setEdges(previousState.edges);
    }
  }, [undo, setNodes, setEdges]);

  const handleRedo = useCallback(() => {
    const nextState = redo();
    if (nextState) {
      setNodes(nextState.nodes);
      setEdges(nextState.edges);
    }
  }, [redo, setNodes, setEdges]);

  // Reset handler
  const handleReset = useCallback(() => {
    setShowConfirmReset(true);
  }, []);

  const confirmReset = () => {
    setNodes([]);
    setEdges([]);
    setShowConfirmReset(false);
  };

  // Helper to enrich all nodes (groups + custom nodes) with proper callbacks and icons
  const enrichAllNodes = useCallback((nodes: Node[]) => {
    return nodes.map((node) => {
      // Enrich group nodes
      if (node.type === 'group') {
        const resourceType = node.data?.type || node.data?.resourceType;
        // Always ensure the color is correct based on resource type
        const correctColor = resourceType ? getContainerColor(resourceType) : (node.data?.color || '#F3F4F6');

        return {
          ...node,
          style: {
            ...node.style,
            backgroundColor: correctColor, // Force the correct background color in style
          },
          data: {
            ...node.data,
            color: correctColor, // Force the correct color in data
            onDelete: () => deleteGroupWithChildren(node.id),
            onUpdate: (label: string, color: string) => {
              setNodes((nds) =>
                nds.map((n) =>
                  n.id === node.id ? {
                    ...n,
                    style: { ...n.style, backgroundColor: color },
                    data: { ...n.data, label, color }
                  } : n
                )
              );
            },
          },
        };
      }

      // Enrich custom nodes (add onUpdateLabel and resolve icon)
      if (node.type === 'custom' && !node.data?.onUpdateLabel) {
        const type = node.data?.type || node.data?.resourceType;
        const icon = resolveIconFromType(type);

        return {
          ...node,
          data: {
            ...node.data,
            icon: icon || node.data?.icon,
            onUpdateLabel: (newLabel: string) => {
              setNodes((nds) =>
                nds.map((n) =>
                  n.id === node.id ? { ...n, data: { ...n.data, label: newLabel, editing: false } } : n
                )
              );
            },
          },
        };
      }

      return node;
    });
  }, [deleteGroupWithChildren, setNodes]);

  // Helper function to sort nodes so parents come before children (for correct z-index)
  const sortNodesByHierarchy = useCallback((nodesToSort: Node[]): Node[] => {
    const nodeMap = new Map(nodesToSort.map(n => [n.id, n]));
    const sorted: Node[] = [];
    const visited = new Set<string>();

    function visit(node: Node) {
      if (visited.has(node.id)) return;
      // If this node has a parent, visit the parent first
      if (node.parentNode && nodeMap.has(node.parentNode)) {
        visit(nodeMap.get(node.parentNode)!);
      }
      visited.add(node.id);
      sorted.push(node);
    }

    nodesToSort.forEach(node => visit(node));
    return sorted;
  }, []);

  // Helper function to encapsulate nodes inside their referenced containers
  const encapsulateNodesInContainers = useCallback((nodesToProcess: Node[]): Node[] => {
    // Container resource types that can hold other resources
    // GCP hierarchy: Folder > Project > Network > Subnetwork > Resources
    const containerTypes = [
      'azurerm_resource_group', 'azurerm_virtual_network', 'azurerm_subnet',
      'aws_vpc', 'aws_subnet',
      'google_folder', 'google_project', 'google_compute_network', 'google_compute_subnetwork',
      'fabric_capacity', 'fabric_workspace', 'fabric_folder',
    ];

    // Map of reference patterns to container types (provider-aware)
    // For params that exist in multiple providers, we'll check the node type
    const referenceToContainer: Record<string, string | Record<string, string>> = {
      // Azure
      'resource_group_name': 'azurerm_resource_group',
      'virtual_network_name': 'azurerm_virtual_network',
      // AWS
      'vpc_id': 'aws_vpc',
      // Multi-provider: subnet_id exists in both AWS and Azure
      'subnet_id': { 'aws': 'aws_subnet', 'azure': 'azurerm_subnet' },
      // GCP
      'folder_id': 'google_folder',    // Projects and folders can be in folders
      'project': 'google_project',
      'network': 'google_compute_network',
      'subnetwork': 'google_compute_subnetwork',
    };

    // Find all container nodes
    const containerNodes = nodesToProcess.filter(n =>
      n.type === 'group' && containerTypes.includes(n.data?.type || n.data?.resourceType)
    );

    if (containerNodes.length === 0) return nodesToProcess;

    // Process each node to check if it should be encapsulated
    // Note: Groups can also be children (e.g., google_project inside google_folder)
    const processedNodes = nodesToProcess.map(node => {
      // Skip if already has a parent
      if (node.parentNode) return node;

      // Check both params and config for reference values
      const nodeParams = { ...(node.data?.config || {}), ...(node.data?.params || {}) };
      const nodeType = node.data?.type || node.data?.resourceType || '';

      // Check each reference parameter
      for (const [paramName, containerTypeOrMap] of Object.entries(referenceToContainer)) {
        const paramValue = nodeParams[paramName];
        if (!paramValue || typeof paramValue !== 'string') continue;

        // Determine the expected container type based on node's provider
        let expectedContainerType: string;
        if (typeof containerTypeOrMap === 'object') {
          // Multi-provider param (like subnet_id)
          if (nodeType.startsWith('aws_')) {
            expectedContainerType = containerTypeOrMap['aws'];
          } else if (nodeType.startsWith('azurerm_')) {
            expectedContainerType = containerTypeOrMap['azure'];
          } else {
            continue; // Skip if provider doesn't match
          }
        } else {
          expectedContainerType = containerTypeOrMap;
          // Check provider match for single-provider params
          if (paramName === 'resource_group_name' && !nodeType.startsWith('azurerm_')) continue;
          if (paramName === 'vpc_id' && !nodeType.startsWith('aws_')) continue;
          if (paramName === 'virtual_network_name' && !nodeType.startsWith('azurerm_')) continue;
          // GCP-specific provider checks
          if (paramName === 'folder_id' && !nodeType.startsWith('google_')) continue;
          if (paramName === 'project' && !nodeType.startsWith('google_')) continue;
          if (paramName === 'network' && !nodeType.startsWith('google_')) continue;
          if (paramName === 'subnetwork' && !nodeType.startsWith('google_')) continue;
        }

        // Extract resource name from reference pattern ${type.name.attribute}
        const match = paramValue.match(/\$\{([^.]+)\.([^.]+)\./);
        if (!match) continue;

        const [, refType, refName] = match;

        // Verify the reference matches the expected container type
        if (refType !== expectedContainerType) continue;

        // Find matching container
        const matchingContainer = containerNodes.find(c => {
          const cType = c.data?.type || c.data?.resourceType;
          const cLabel = c.data?.label || c.data?.name || '';
          const cTfName = cLabel.toLowerCase().replace(/[^a-z0-9_]/g, '_');
          return cType === refType && cTfName === refName;
        });

        if (matchingContainer) {
          // Calculate relative position inside container
          const relativePosition = {
            x: node.position.x - matchingContainer.position.x,
            y: node.position.y - matchingContainer.position.y
          };

          // Ensure position is within container bounds
          const containerWidth = matchingContainer.width || 400;
          const containerHeight = matchingContainer.height || 250;
          const safePosition = {
            x: Math.max(20, Math.min(relativePosition.x, containerWidth - 100)),
            y: Math.max(40, Math.min(relativePosition.y, containerHeight - 80))
          };

          return {
            ...node,
            position: safePosition,
            parentNode: matchingContainer.id,
            extent: 'parent' as const,
            expandParent: true
          };
        }
      }

      return node;
    });

    // Sort nodes so parents come before children (for correct z-index rendering)
    return sortNodesByHierarchy(processedNodes);
  }, [sortNodesByHierarchy]);

  // Helper function to create edges from resource references in node params
  const createEdgesFromReferences = useCallback((
    updatedNode: Node,
    allNodes: Node[],
    currentEdges: Edge[]
  ): Edge[] => {
    const newEdges: Edge[] = [...currentEdges];
    const nodeParams = updatedNode.data?.params || {};

    // Scan all params for reference patterns like ${type.name.attribute}
    for (const [paramName, paramValue] of Object.entries(nodeParams)) {
      if (typeof paramValue !== 'string') continue;

      // Match reference pattern: ${resource_type.resource_name.attribute}
      const match = paramValue.match(/\$\{([^.]+)\.([^.]+)\.([^}]+)\}/);
      if (!match) continue;

      const [, refType, refName] = match;

      // Find the target node that matches this reference
      const targetNode = allNodes.find(n => {
        const nType = n.data?.type || n.data?.resourceType;
        const nLabel = n.data?.label || n.data?.name || '';
        const tfName = nLabel.toLowerCase().replace(/[^a-z0-9_]/g, '_');
        return nType === refType && tfName === refName;
      });

      if (!targetNode) continue;

      // Check if edge already exists
      const edgeId = `ref-${updatedNode.id}-${targetNode.id}-${paramName}`;
      const existingEdge = newEdges.find(e =>
        (e.source === updatedNode.id && e.target === targetNode.id) ||
        e.id === edgeId
      );

      if (!existingEdge) {
        console.log(`🔗 Creating edge from ${updatedNode.data?.label} to ${targetNode.data?.label} (${paramName})`);

        newEdges.push({
          id: edgeId,
          source: updatedNode.id,
          target: targetNode.id,
          type: 'smoothstep',
          animated: false,
          label: paramName.replace(/_/g, ' '),
          labelStyle: { fontSize: 10, fill: '#6b7280' },
          labelBgStyle: { fill: 'white', fillOpacity: 0.8 },
          style: { stroke: canvasEdgeColor, strokeWidth: 2 },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: canvasEdgeColor,
          },
          data: {
            dependencyType: 'property_reference',
            property: paramName,
            autoCreated: true
          },
          zIndex: 1000
        } as Edge);
      }
    }

    return newEdges;
  }, []);

  // Handler to auto-create missing resources
  const handleAutoCreateResources = useCallback(async (resources: any[]) => {
    try {
      const updated = await createMissingResources(resources, { nodes, edges });
      if (updated) {
        console.log('✅ Auto-created missing resources');

        // Encapsulate nodes that reference newly created containers
        const encapsulatedNodes = encapsulateNodesInContainers(updated.nodes);

        // Enrich all nodes (groups + custom) with proper callbacks
        const enrichedNodes = enrichAllNodes(encapsulatedNodes);
        setNodes(enrichedNodes);
        setEdges(updated.edges);
        markTabDirty(activeTabId || '', true);
      }
    } catch (error) {
      console.error('Failed to create missing resources:', error);
    }
  }, [createMissingResources, nodes, edges, activeTabId, markTabDirty, enrichAllNodes, encapsulateNodesInContainers]);

  // Handler to select and focus on a node from validation panel
  const handleSelectNodeFromValidation = useCallback((nodeId: string) => {
    const node = nodes.find(n => n.id === nodeId);
    if (node && reactFlowInstance) {
      // Center the node in view
      reactFlowInstance.setCenter(node.position.x + 100, node.position.y + 50, { zoom: 1.5, duration: 800 });

      // Highlight the node by selecting it
      setNodes((nds) =>
        nds.map((n) => ({
          ...n,
          selected: n.id === nodeId,
        }))
      );
    }
  }, [nodes, reactFlowInstance, setNodes]);

  // Handler for edge click
  const onEdgeClick = useCallback((event: React.MouseEvent, edge: Edge) => {
    event.stopPropagation();
    setSelectedEdge(edge);
  }, []);

  // Handler to update edge settings
  const handleUpdateEdge = useCallback((edgeId: string, updates: Partial<Edge>) => {
    setEdges((eds) =>
      eds.map((edge) =>
        edge.id === edgeId
          ? { ...edge, ...updates }
          : edge
      )
    );
    markTabDirty(activeTabId || '', true);
  }, [setEdges, activeTabId, markTabDirty]);

  // Alignment functions
  const alignNodesLeft = useCallback(() => {
    const selected = nodes.filter(n => selectedNodes.includes(n.id));
    if (selected.length < 2) return;
    const minX = Math.min(...selected.map(n => n.position.x));
    const aligned = nodes.map(n =>
      selectedNodes.includes(n.id) ? { ...n, position: { ...n.position, x: minX } } : n
    );
    setNodes(aligned);
  }, [nodes, selectedNodes, setNodes]);

  const alignNodesCenter = useCallback(() => {
    const selected = nodes.filter(n => selectedNodes.includes(n.id));
    if (selected.length < 2) return;
    const avgX = selected.reduce((sum, n) => sum + n.position.x, 0) / selected.length;
    const aligned = nodes.map(n =>
      selectedNodes.includes(n.id) ? { ...n, position: { ...n.position, x: avgX } } : n
    );
    setNodes(aligned);
  }, [nodes, selectedNodes, setNodes]);

  const alignNodesRight = useCallback(() => {
    const selected = nodes.filter(n => selectedNodes.includes(n.id));
    if (selected.length < 2) return;
    const maxX = Math.max(...selected.map(n => n.position.x));
    const aligned = nodes.map(n =>
      selectedNodes.includes(n.id) ? { ...n, position: { ...n.position, x: maxX } } : n
    );
    setNodes(aligned);
  }, [nodes, selectedNodes, setNodes]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts if user is typing in an input field
      const target = e.target as HTMLElement;
      const isTyping = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';

      // Delete or Backspace to remove selected nodes/edges
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (isTyping) return;
        e.preventDefault();

        // Get selected nodes and edges
        const selectedNodesList = nodes.filter(n => n.selected);
        const selectedEdgesList = edges.filter(e => e.selected);

        if (selectedNodesList.length > 0 || selectedEdgesList.length > 0) {
          // Handle group deletions separately
          const groupsToDelete = selectedNodesList.filter(n => n.type === 'group');
          const regularNodesToDelete = selectedNodesList.filter(n => n.type !== 'group');

          // Delete groups with special handler
          groupsToDelete.forEach(group => deleteGroupWithChildren(group.id));

          // Delete regular nodes normally
          if (regularNodesToDelete.length > 0) {
            const regularNodeIds = regularNodesToDelete.map(n => n.id);
            setNodes((nds) => nds.filter((n) => !regularNodeIds.includes(n.id)));
          }

          // Remove selected edges and edges connected to all deleted nodes
          const allDeletedIds = selectedNodesList.map(n => n.id);
          setEdges((eds) => eds.filter((e) =>
            !selectedEdgesList.map(edge => edge.id).includes(e.id) &&
            !allDeletedIds.includes(e.source) &&
            !allDeletedIds.includes(e.target)
          ));
        }
      }
      // P or Enter to open properties of selected node
      else if ((e.key === 'p' || e.key === 'P' || e.key === 'Enter') && !isTyping) {
        const selectedNodesList = nodes.filter(n => n.selected);
        if (selectedNodesList.length === 1) {
          e.preventDefault();
          setDockNode(selectedNodesList[0]);
          closeContextMenu();
        }
      }
      // R to rename selected node
      else if ((e.key === 'r' || e.key === 'R') && !isTyping) {
        const selectedNodesList = nodes.filter(n => n.selected);
        if (selectedNodesList.length === 1) {
          e.preventDefault();
          const node = selectedNodesList[0];
          const updated = nodes.map((n) =>
            n.id === node.id
              ? {
                  ...n,
                  data: {
                    ...n.data,
                    editing: true,
                    onUpdateLabel: (newLabel: string) => {
                      setNodes((nds) =>
                        nds.map((node) =>
                          node.id === n.id ? { ...node, data: { ...node.data, label: newLabel, editing: false } } : node
                        )
                      );
                    },
                  },
                }
              : n
          );
          setNodes(updated);
          closeContextMenu();
        }
      }
      // Undo/Redo
      else if (e.ctrlKey || e.metaKey) {
        if (e.key === 'z' && !e.shiftKey) {
          e.preventDefault();
          handleUndo();
        } else if ((e.key === 'y') || (e.key === 'z' && e.shiftKey)) {
          e.preventDefault();
          handleRedo();
        }
        // Ctrl+D to duplicate
        else if (e.key === 'd' || e.key === 'D') {
          const selectedNodesList = nodes.filter(n => n.selected);
          if (selectedNodesList.length === 1) {
            e.preventDefault();
            const node = selectedNodesList[0];
            const newNode: Node = {
              ...node,
              id: getId(),
              position: { x: node.position.x + 40, y: node.position.y + 40 },
            };
            setNodes((nds) => [...nds, newNode]);
            closeContextMenu();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo, nodes, edges, setNodes, setEdges, deleteGroupWithChildren]);

  // Track selected nodes
  const onSelectionChange = useCallback((params: any) => {
    setSelectedNodes(params.nodes.map((n: Node) => n.id));

    // Afficher le menu quand un seul node est sélectionné
    if (params.nodes.length === 1 && reactFlowWrapper.current) {
      const selectedNode = params.nodes[0];

      // Attendre un peu que le DOM soit mis à jour
      setTimeout(() => {
        if (!reactFlowWrapper.current) return;

        // Trouver l'élément DOM du node sélectionné
        const nodeElement = document.querySelector(`[data-id="${selectedNode.id}"]`);
        if (!nodeElement) return;

        const wrapperRect = reactFlowWrapper.current.getBoundingClientRect();
        const nodeRect = nodeElement.getBoundingClientRect();

        // Positionner le menu EN BAS à DROITE du composant
        const position = {
          x: nodeRect.right - wrapperRect.left + 15, // 15px à droite du bord droit
          y: nodeRect.bottom - wrapperRect.top + 5, // 5px en dessous du bas
        };

        setContextMenu({ node: selectedNode, position });
      }, 50);
    } else if (params.nodes.length === 0) {
      // Fermer le menu si aucun node n'est sélectionné
      closeContextMenu();
    }
  }, []);

  // Force ReactFlow to resize when dock opens/closes
  useEffect(() => {
    if (reactFlowInstance) {
      // Trigger window resize event to force ReactFlow to recalculate
      window.dispatchEvent(new Event('resize'));

      // Small delay to ensure DOM has updated, then fit view
      setTimeout(() => {
        reactFlowInstance.fitView({ padding: 0.1, duration: 200 });
      }, 250);
    }
  }, [dockOpen, reactFlowInstance]);

  // Add annotation
  const addAnnotation = useCallback(() => {
    if (!reactFlowInstance) return;

    const id = getId();
    const newNode: Node = {
      id,
      type: 'annotation',
      position: { x: 100, y: 100 },
      data: {
        text: t('flowEditor.defaults.newNote'),
        color: '#FEF3C7',
        onUpdate: (text: string, color: string) => {
          setNodes((nds) =>
            nds.map((n) =>
              n.id === id ? { ...n, data: { ...n.data, text, color } } : n
            )
          );
        },
        onDelete: () => {
          setNodes((nds) => nds.filter((n) => n.id !== id));
        }
      },
    };
    setNodes((nds) => [...nds, newNode]);
  }, [reactFlowInstance, setNodes]);

  // Add group
  const addGroup = useCallback(() => {
    if (!reactFlowInstance) return;

    const id = getId();
    const newNode: Node = {
      id,
      type: 'group',
      position: { x: 150, y: 150 },
      width: 500,
      height: 300,
      data: {
        label: t('flowEditor.defaults.newGroup'),
        color: '#DBEAFE',
        onUpdate: (label: string, color: string) => {
          setNodes((nds) =>
            nds.map((n) =>
              n.id === id ? { ...n, data: { ...n.data, label, color } } : n
            )
          );
        },
        onDelete: () => deleteGroupWithChildren(id)
      },
    };
    setNodes((nds) => [...nds, newNode]);
  }, [reactFlowInstance, setNodes, deleteGroupWithChildren]);

  // Apply pattern (supports nested parentNode hierarchy)
  const applyPattern = useCallback((pattern: any) => {
    if (!reactFlowInstance) return;

    const offsetX = 60;
    const offsetY = 60;

    // Build old→new ID map in one pass
    const nodeIdMap = new Map<string, string>(
      pattern.nodes.map((n: any) => [n.id, getId()])
    );

    const rawNodes = pattern.nodes.map((node: any) => {
      const newId = nodeIdMap.get(node.id)!;
      const newParentId = node.parentNode ? nodeIdMap.get(node.parentNode) : undefined;
      return {
        ...node,
        id: newId,
        // Only offset top-level nodes; children positions are relative to parent
        position: newParentId
          ? { ...node.position }
          : { x: node.position.x + offsetX, y: node.position.y + offsetY },
        ...(newParentId
          ? { parentNode: newParentId, extent: 'parent' as const, expandParent: true }
          : { parentNode: undefined, extent: undefined, expandParent: undefined }),
      };
    });

    // Enrich with proper callbacks + icons
    const enrichedNodes = enrichAllNodes(rawNodes);
    // Sort so parents come before children (ReactFlow requirement)
    const sortedNodes = sortNodesByHierarchy(enrichedNodes);

    const newEdges = pattern.edges.map((edge: any) => ({
      ...edge,
      id: getId(),
      source: nodeIdMap.get(edge.source),
      target: nodeIdMap.get(edge.target),
      type: 'smoothstep',
      style: { stroke: canvasEdgeColor, strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: canvasEdgeColor },
    }));

    setNodes((nds) => [...nds, ...sortedNodes]);
    setEdges((eds) => [...eds, ...newEdges]);
  }, [reactFlowInstance, setNodes, setEdges, enrichAllNodes, sortNodesByHierarchy, canvasEdgeColor]);

  return (
    <div
      ref={reactFlowWrapper}
      style={{
        width: "100%",
        height: "100%",
        background: backgroundColorEnabled
          ? backgroundColor
          : gridVisible
          ? `linear-gradient(to right, #e0e0e0 1px, transparent 1px),
             linear-gradient(to bottom, #e0e0e0 1px, transparent 1px)`
          : "transparent",
        backgroundSize: gridVisible ? `${gridSize}px ${gridSize}px` : "0px 0px",
        position: "relative",
      }}
    >
      {/* Toolbar portal — rendu dans CanvasHeader via createPortal */}
      {toolbarPortalEl && createPortal(
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('canvas-save'))}
          style={{
            padding: '8px 14px',
            border: 'none',
            background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)',
            borderRadius: 6,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            color: '#fff',
            fontWeight: 600,
            fontSize: 13,
            boxShadow: '0 2px 8px rgba(59,130,246,0.35)',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)'; }}
          title={t('flowEditor.toolbar.saveTitle')}
        >
          <FiSave size={15} />
          <span>{t('flowEditor.toolbar.save')}</span>
        </button>
        <div style={{ width: 1, height: 32, background: '#e0e0e0', margin: '0 4px' }} />
        <button
          onClick={handleUndo}
          disabled={!canUndo}
          style={{
            padding: '8px 12px',
            border: 'none',
            background: canUndo ? '#f0f0f0' : '#fafafa',
            borderRadius: 6,
            cursor: canUndo ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            opacity: canUndo ? 1 : 0.5
          }}
          title={t('flowEditor.toolbar.undoTitle')}
        >
          <FiRotateCcw size={16} />
          <span style={{ fontSize: 13 }}>{t('flowEditor.toolbar.undo')}</span>
        </button>
        <button
          onClick={handleRedo}
          disabled={!canRedo}
          style={{
            padding: '8px 12px',
            border: 'none',
            background: canRedo ? '#f0f0f0' : '#fafafa',
            borderRadius: 6,
            cursor: canRedo ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            opacity: canRedo ? 1 : 0.5
          }}
          title={t('flowEditor.toolbar.redoTitle')}
        >
          <FiRotateCw size={16} />
          <span style={{ fontSize: 13 }}>{t('flowEditor.toolbar.redo')}</span>
        </button>
        <div style={{ width: 1, height: 32, background: '#e0e0e0', margin: '0 4px' }} />
        <button
          onClick={handleReset}
          style={{
            padding: '8px 12px',
            border: 'none',
            background: '#FEE2E2',
            borderRadius: 6,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            color: '#DC2626',
            fontWeight: 500
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#FECACA';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#FEE2E2';
          }}
          title={t('flowEditor.toolbar.resetTitle')}
        >
          <FiRefreshCw size={16} />
          <span style={{ fontSize: 13 }}>{t('flowEditor.toolbar.reset')}</span>
        </button>
        <div style={{ width: 1, height: 32, background: '#e0e0e0', margin: '0 4px' }} />
        <button
          onClick={alignNodesLeft}
          disabled={selectedNodes.length < 2}
          style={{
            padding: '8px 12px',
            border: 'none',
            background: selectedNodes.length >= 2 ? '#f0f0f0' : '#fafafa',
            borderRadius: 6,
            cursor: selectedNodes.length >= 2 ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            opacity: selectedNodes.length >= 2 ? 1 : 0.5
          }}
          title={t('flowEditor.toolbar.alignLeft')}
        >
          <FiAlignLeft size={16} />
        </button>
        <button
          onClick={alignNodesCenter}
          disabled={selectedNodes.length < 2}
          style={{
            padding: '8px 12px',
            border: 'none',
            background: selectedNodes.length >= 2 ? '#f0f0f0' : '#fafafa',
            borderRadius: 6,
            cursor: selectedNodes.length >= 2 ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            opacity: selectedNodes.length >= 2 ? 1 : 0.5
          }}
          title={t('flowEditor.toolbar.alignCenter')}
        >
          <FiAlignCenter size={16} />
        </button>
        <button
          onClick={alignNodesRight}
          disabled={selectedNodes.length < 2}
          style={{
            padding: '8px 12px',
            border: 'none',
            background: selectedNodes.length >= 2 ? '#f0f0f0' : '#fafafa',
            borderRadius: 6,
            cursor: selectedNodes.length >= 2 ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            opacity: selectedNodes.length >= 2 ? 1 : 0.5
          }}
          title={t('flowEditor.toolbar.alignRight')}
        >
          <FiAlignRight size={16} />
        </button>
        <div style={{ width: 1, height: 32, background: '#e0e0e0', margin: '0 4px' }} />
        {selectedNodes.length >= 2 && (
          <button
            onClick={deleteSelectedNodes}
            style={{
              padding: '8px 12px',
              border: 'none',
              background: '#FEE2E2',
              borderRadius: 6,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              color: '#B91C1C',
              fontWeight: 600,
            }}
            title={`Supprimer la sélection (${selectedNodes.length} éléments) — ou touche Suppr`}
          >
            <FiTrash2 size={14} />
            <span style={{ fontSize: 12 }}>{selectedNodes.length}</span>
          </button>
        )}
        <div style={{ width: 1, height: 32, background: '#e0e0e0', margin: '0 4px' }} />
        <button
          onClick={addAnnotation}
          style={{ padding: '6px 10px', border: 'none', background: '#f0f0f0', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}
          title={t('flowEditor.toolbar.addNote')}
        >
          <FiMessageSquare size={15} />
          <span style={{ fontSize: 12 }}>{t('flowEditor.toolbar.note')}</span>
        </button>
        <button
          onClick={addGroup}
          style={{ padding: '6px 10px', border: 'none', background: '#f0f0f0', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}
          title={t('flowEditor.toolbar.addGroup')}
        >
          <FiPackage size={15} />
          <span style={{ fontSize: 12 }}>{t('flowEditor.toolbar.group')}</span>
        </button>
        <button
          onClick={() => setShowTemplatesPanel(!showTemplatesPanel)}
          style={{ padding: '6px 10px', border: 'none', background: showTemplatesPanel ? '#038387' : '#f0f0f0', color: showTemplatesPanel ? 'white' : '#374151', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.2s' }}
          title="Templates d'architecture"
        >
          <FiLayers size={15} />
          <span style={{ fontSize: 12 }}>Templates</span>
        </button>
        <div style={{ width: 1, height: 28, background: '#e0e0e0', margin: '0 4px' }} />
        <button
          onClick={() => setShowValidation(!showValidation)}
          style={{ padding: '6px 10px', border: 'none', background: showValidation ? '#3B82F6' : '#f0f0f0', color: showValidation ? 'white' : '#374151', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.2s' }}
          title={showValidation ? t('flowEditor.toolbar.hideValidation') : t('flowEditor.toolbar.showValidation')}
        >
          <FiCheckSquare size={15} />
          <span style={{ fontSize: 12 }}>{t('flowEditor.toolbar.validation')}</span>
          {validationIssues.length > 0 && (
            <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 5px', background: showValidation ? 'rgba(255,255,255,0.3)' : '#EF4444', color: 'white', borderRadius: 10 }}>
              {validationIssues.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setShowFinOps(!showFinOps)}
          style={{ padding: '6px 10px', border: 'none', background: showFinOps ? '#038387' : '#f0f0f0', color: showFinOps ? 'white' : '#374151', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.2s' }}
          title="Estimations de coût (FinOps)"
        >
          <FiDollarSign size={15} />
          <span style={{ fontSize: 12 }}>FinOps</span>
        </button>
        <button
          onClick={() => setShowLineage(!showLineage)}
          style={{ padding: '6px 10px', border: 'none', background: showLineage ? '#2563EB' : '#f0f0f0', color: showLineage ? 'white' : '#374151', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.2s' }}
          title="Vue Lineage — flux de données Fabric"
        >
          <FiGitMerge size={15} />
          <span style={{ fontSize: 12 }}>Lineage</span>
        </button>
        <button
          onClick={() => setShowVariables(!showVariables)}
          disabled={!activeTabId}
          style={{ padding: '6px 10px', border: 'none', background: showVariables ? '#6366F1' : '#f0f0f0', color: showVariables ? 'white' : '#374151', borderRadius: 6, cursor: activeTabId ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.2s', opacity: activeTabId ? 1 : 0.5 }}
          title={showVariables ? t('flowEditor.toolbar.hideVariables', 'Hide Variables') : t('flowEditor.toolbar.showVariables', 'Show Variables')}
        >
          <FiSliders size={15} />
          <span style={{ fontSize: 12 }}>{t('flowEditor.toolbar.variables', 'Variables')}</span>
        </button>
        <button
          onClick={() => {
            if (!showDependencyPanel) analyzeCurrentDiagram({ nodes, edges });
            setShowDependencyPanel(!showDependencyPanel);
          }}
          style={{ padding: '6px 10px', border: 'none', background: showDependencyPanel ? '#F59E0B' : hasDependencyIssues ? '#FEF3C7' : '#f0f0f0', color: showDependencyPanel ? 'white' : hasDependencyIssues ? '#D97706' : '#374151', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.2s', position: 'relative' }}
          title={showDependencyPanel ? t('flowEditor.toolbar.hideDependencies', 'Hide Dependencies') : t('flowEditor.toolbar.showDependencies', 'Show Dependencies')}
        >
          <FiAlertTriangle size={15} />
          <span style={{ fontSize: 12 }}>{t('flowEditor.toolbar.dependencies', 'Dependencies')}</span>
          {hasDependencyIssues && !showDependencyPanel && (
            <span style={{ position: 'absolute', top: -4, right: -4, background: '#EF4444', color: 'white', fontSize: 10, fontWeight: 600, borderRadius: '50%', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {missingResources.length}
            </span>
          )}
        </button>
      </div>, toolbarPortalEl)}

      {/* Patterns Library — shown when toolbar Templates button is active */}
      {showTemplatesPanel && (
        <PatternsLibrary
          onApplyPattern={applyPattern}
          onOpenWizard={() => { setShowTemplatesPanel(false); setShowMultiDomainWizard(true); }}
          open={showTemplatesPanel}
          onClose={() => setShowTemplatesPanel(false)}
        />
      )}

      {/* Multi-Domain Wizard */}
      <MultiDomainWizardModal
        isOpen={showMultiDomainWizard}
        onClose={() => setShowMultiDomainWizard(false)}
        onComplete={({ diagramData }) => {
          applyPattern(diagramData);
          setShowMultiDomainWizard(false);
        }}
      />

      <div
        style={{ width: dockOpen ? 'calc(100% - 400px)' : '100%', height: '100%', transition: 'width 220ms ease', position: 'relative' }}
        onMouseMove={onCanvasMouseMove}
      >
        {/* Connection preview overlay — above GroupNode HTML layer */}
        {connPreview && (
          <svg style={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            pointerEvents: 'none', zIndex: 9999,
          }}>
            <polyline
              points={`${connPreview.startX},${connPreview.startY} ${connPreview.startX + (connPreview.curX - connPreview.startX) / 2},${connPreview.startY} ${connPreview.startX + (connPreview.curX - connPreview.startX) / 2},${connPreview.curY} ${connPreview.curX},${connPreview.curY}`}
              fill="none"
              stroke={canvasEdgeColor}
              strokeWidth={2}
              strokeDasharray="8 4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx={connPreview.curX} cy={connPreview.curY} r={5} fill={canvasEdgeColor} stroke="white" strokeWidth={1.5} />
          </svg>
        )}
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onInit={setReactFlowInstance}
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          onNodeDragStop={onNodeDragStop}
          onNodeContextMenu={handleNodeContextMenu}
          onNodeDoubleClick={handleNodeDoubleClick}
          onEdgeClick={onEdgeClick}
          onSelectionChange={onSelectionChange}
          onPaneClick={closeContextMenu}
          fitView
          nodeTypes={nodeTypes}
          style={{ width: '100%', height: '100%' }}
          connectionLineStyle={{ stroke: 'transparent' }}
          onConnectStart={onConnectStart}
          onConnectEnd={onConnectEnd}
        >
          <MiniMap zoomable pannable style={{ background: '#f5f5f5' }} />
          <Controls showZoom showFitView showInteractive />
        </ReactFlow>
      </div>

      {/* Menu contextuel */}
      {contextMenu.node && (
        <div
          className="context-menu"
          style={{
            top: contextMenu.position.y,
            left: contextMenu.position.x,
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.preventDefault()}
        >
          <div
            className="context-menu-item"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); handleSettingsClick(); }}
          >
            <FiSettings className="context-menu-icon" />
            <span>{t('flowEditor.contextMenu.settings')}</span>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#999' }}>P</span>
          </div>
          <div className="context-menu-item" onClick={handleRename}>
            <FiEdit2 className="context-menu-icon" />
            <span>{t('flowEditor.contextMenu.rename')}</span>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#999' }}>R</span>
          </div>
          <div className="context-menu-item" onClick={handleDuplicate}>
            <FiCopy className="context-menu-icon" />
            <span>{t('flowEditor.contextMenu.duplicate')}</span>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#999' }}>Ctrl+D</span>
          </div>
          <div className="context-menu-separator" />
          <div className="context-menu-item danger" onClick={handleDelete}>
            <FiTrash2 className="context-menu-icon" />
            <span>{t('flowEditor.contextMenu.delete')}</span>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#999' }}>Del</span>
          </div>
        </div>
      )}

      {/* Petit panneau flottant */}
      {settingsNode && (
        <SettingsPanel
          node={settingsNode}
          onClose={() => {
            setSettingsNode(null);
            setPanelPosition(null);
          }}
          onUpdate={(updatedNode) => {
            // Update node, check for encapsulation, and create edges from references
            setNodes((nds) => {
              const updatedNodes = nds.map((n) => (n.id === updatedNode.id ? updatedNode : n));
              // Apply encapsulation logic
              const encapsulatedNodes = encapsulateNodesInContainers(updatedNodes);
              // Create edges from references
              setEdges((currentEdges) => createEdgesFromReferences(updatedNode, encapsulatedNodes, currentEdges));
              return encapsulatedNodes;
            });
            setSettingsNode(null);
            setPanelPosition(null);
          }}
          onExpandToDock={(n) => {
            setDockNode(n);
            setSettingsNode(null);
            setPanelPosition(null);
          }}
          mode="floating"
          availableNodes={nodes}
        />
      )}

      {/* Validation Panel */}
      {showValidation && (
        <ValidationPanel
          issues={validationIssues}
          onClose={() => setShowValidation(false)}
          onSelectNode={handleSelectNodeFromValidation}
        />
      )}

      {/* FinOps Panel */}
      {showFinOps && (
        <FinOpsPanel
          nodes={nodes}
          onClose={() => setShowFinOps(false)}
        />
      )}

      {/* Fabric Lineage Panel */}
      {showLineage && (
        <FabricLineagePanel
          nodes={nodes}
          onClose={() => setShowLineage(false)}
          onSelectNode={(nodeId) => {
            const node = nodes.find(n => n.id === nodeId);
            if (node) handleSelectNodeFromValidation(nodeId);
          }}
        />
      )}

      {/* Connection Suggestions Button - Floating indicator */}
      {connectionSuggestions && connectionSuggestions.suggestions.length > 0 && (
        <ConnectionSuggestionsButton
          suggestionsCount={connectionSuggestions.suggestions.length}
          criticalCount={connectionSuggestions.suggestions.filter(s => s.severity === 'critical').length}
          onClick={() => setShowSuggestionsDrawer(true)}
          onDismiss={() => {
            setConnectionSuggestions(null);
            setShowSuggestionsDrawer(false);
          }}
        />
      )}

      {/* Connection Suggestions Drawer */}
      {connectionSuggestions && (
        <ConnectionSuggestionsDrawer
          isOpen={showSuggestionsDrawer}
          sourceNode={connectionSuggestions.sourceNode}
          targetNode={connectionSuggestions.targetNode}
          suggestions={connectionSuggestions.suggestions}
          onCreateResource={({ node, edges: newEdges }) => {
            // Calculate new states
            const updatedNodes = [...nodes, node];
            const updatedEdges = [...edges, ...newEdges];

            // Add the new node and its connecting edges to the canvas
            setNodes(updatedNodes);
            setEdges(updatedEdges);

            // Save to history
            takeSnapshot(updatedNodes, updatedEdges);

            // Close drawer after creating resource
            setShowSuggestionsDrawer(false);
          }}
          onClose={() => {
            // Fermer le drawer mais garder les suggestions
            // pour pouvoir rouvrir via le bouton
            setShowSuggestionsDrawer(false);
          }}
        />
      )}

      {/* Variables Panel */}
      {showVariables && activeTabId && (
        <VariablesPanel
          architectureId={activeTabId}
          onClose={() => setShowVariables(false)}
        />
      )}

      {/* Dependency Detection Panel */}
      {showDependencyPanel && (
        <DependencyDetector
          missingResources={missingResources}
          suggestions={suggestions}
          onAutoCreate={handleAutoCreateResources}
          onClose={() => setShowDependencyPanel(false)}
          isLoading={isAnalyzingDeps}
          currentNodes={nodes}
        />
      )}

      {/* Panneau de propriétés docké à droite - position absolue */}
      <RightSettingsDrawer
        open={dockOpen}
        node={dockNode}
        onClose={() => setDockNode(null)}
        onUpdate={(updated) => {
          // Update node, check for encapsulation, and create edges from references
          setNodes((nds) => {
            const updatedNodes = nds.map((n) => (n.id === updated.id ? updated : n));
            // Apply encapsulation logic
            const encapsulatedNodes = encapsulateNodesInContainers(updatedNodes);
            // Create edges from references
            setEdges((currentEdges) => createEdgesFromReferences(updated, encapsulatedNodes, currentEdges));
            return encapsulatedNodes;
          });
          setDockNode(updated);
        }}
        availableNodes={nodes}
      />

      {/* Confirmation Dialogs */}
      <ConfirmDialog
        isOpen={showConfirmReset}
        title="Réinitialiser le canvas"
        message="Êtes-vous sûr de vouloir supprimer tous les composants du canvas ? Cette action est irréversible."
        confirmText="Réinitialiser"
        cancelText="Annuler"
        variant="danger"
        onConfirm={confirmReset}
        onCancel={() => setShowConfirmReset(false)}
      />

      <ConfirmDialog
        isOpen={showConfirmDelete}
        title="Supprimer le composant"
        message={`Êtes-vous sûr de vouloir supprimer le composant "${nodeToDelete?.data?.label || 'ce composant'}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowConfirmDelete(false);
          setNodeToDelete(null);
        }}
      />

      {/* Edge Settings Panel */}
      {selectedEdge && (
        <EdgeSettingsPanel
          edge={selectedEdge}
          onClose={() => setSelectedEdge(null)}
          onUpdate={handleUpdateEdge}
        />
      )}
    </div>
  );
}
